import { defineComponent, ref, computed } from 'vue-demi';
import { openBlock, createElementBlock, normalizeClass, renderSlot, resolveComponent, createBlock, withCtx, createCommentVNode } from 'vue';

const InputBoxAddon = [
// Layout
'layout-inline-row',
// Theme
'theme-foreground-outline', 'dark:theme-foreground-outline-dark'];

var script$1 = defineComponent({
  name: 'OrInputBoxAddon',
  props: {
    invalid: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  expose: ['root'],
  setup() {
    // Refs
    const root = ref();
    // Styles
    const rootStyles = computed(() => ['or-input-box-addon-v3', ...InputBoxAddon]);
    return {
      root,
      rootStyles
    };
  }
});

const _hoisted_1$1 = ["invalid", "readonly", "disabled"];
function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles),
    invalid: _ctx.invalid,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled
  }, [renderSlot(_ctx.$slots, "default")], 10 /* CLASS, PROPS */, _hoisted_1$1);
}

script$1.render = render$1;
script$1.__file = "src/components/or-input-box-v3/partials/or-input-box-addon/OrInputBoxAddon.vue";

var InputBoxDirection;
(function (InputBoxDirection) {
  InputBoxDirection["Row"] = "row";
  InputBoxDirection["Column"] = "column";
})(InputBoxDirection || (InputBoxDirection = {}));
var InputBoxVariant;
(function (InputBoxVariant) {
  InputBoxVariant["Styled"] = "styled";
  InputBoxVariant["Unstyled"] = "unstyled";
  InputBoxVariant["Embedded"] = "embedded";
})(InputBoxVariant || (InputBoxVariant = {}));
var InputBoxSize;
(function (InputBoxSize) {
  InputBoxSize["M"] = "m";
  InputBoxSize["S"] = "s";
})(InputBoxSize || (InputBoxSize = {}));

const InputBox = [
// Overflow
'overflow-hidden',
// Box
'w-full',
// Shape
'rounded-md',
// Theme (children)
'children:outline-none'];
const InputBoxDirections = {
  [InputBoxDirection.Row]: [
  // Layout
  'layout-row'],
  [InputBoxDirection.Column]: [
  // Layout
  'layout-column']
};
const InputBoxVariants = {
  [InputBoxVariant.Styled]: [
  // Theme
  'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark', 'theme-foreground-default', 'dark:theme-foreground-default-dark', 'theme-border-outline border-1', 'dark:theme-border-outline-dark', 'theme-outline-transparent outline-2', 'dark:theme-outline-transparent',
  // Theme (focus)
  'focus-within:theme-background-primary-translucent-1', 'dark:focus-within:theme-background-primary-translucent-1-dark', 'focus-within:theme-border-primary focus-within:border-1', 'dark:focus-within:theme-border-primary-dark', 'focus-within:theme-outline-primary focus-within:outline-2', 'dark:focus-within:theme-outline-primary-dark',
  // Theme (invalid)
  'invalid:theme-preset-3-error', 'dark:invalid:theme-preset-3-error-dark', 'invalid:theme-background-error-translucent-1', 'dark:invalid:theme-background-error-translucent-1-dark', 'invalid:theme-foreground-default', 'dark:invalid:theme-foreground-default-dark', 'invalid:theme-border-error invalid:border-1', 'dark:invalid:theme-border-error-dark', 'invalid:theme-outline-transparent invalid:outline-2', 'dark:invalid:theme-outline-transparent',
  // Theme (invalid + focus)
  'invalid:focus-within:theme-background-error-translucent-1', 'dark:invalid:focus-within:theme-background-error-translucent-1-dark', 'invalid:focus-within:theme-border-error invalid:focus-within:border-1', 'dark:invalid:focus-within:theme-border-error-dark', 'invalid:focus-within:theme-outline-error invalid:focus-within:outline-2', 'dark:invalid:focus-within:theme-outline-error-dark',
  // Theme (read-only)
  'read-only:theme-background-primary-translucent-1', 'dark:read-only:theme-background-primary-translucent-1-dark', 'read-only:!border-transparent', 'dark:read-only:!border-transparent',
  // Theme (invalid + read-only)
  'invalid:read-only:bg-error-opacity-0-08', 'dark:invalid:read-only:bg-error-opacity-0-08-dark', 'invalid:read-only:!border-transparent', 'dark:invalid:read-only:!border-transparent'],
  [InputBoxVariant.Unstyled]: [
  // Theme (disabled)
  'disabled:theme-foreground-disabled', 'dark:disabled:theme-foreground-disabled-dark'],
  [InputBoxVariant.Embedded]: [
  // Spacing (children)
  'first:children:!ms-0', 'last:children:!me-0']
};
const InputBoxSizes = {
  'm-row': [
  // Box (children)
  'children:min-h-[46px] md:children:min-h-[34px]',
  // Spacing
  'gap-sm+ md:gap-sm',
  // Spacing (children)
  'first:children:ms-md md:first:children:ms-sm+', 'last:children:me-md md:last:children:me-sm+',
  // Typography
  'typography-body-2-regular'],
  'm-column': [
  // Typography
  'typography-body-2-regular'],
  's-row': [
  // Box (children)
  'children:min-h-[30px]',
  // Spacing
  'gap-[6px]',
  // Spacing (children)
  'first:children:ms-md md:first:children:ms-sm+', 'last:children:me-md md:last:children:me-sm+',
  // Typography
  'typography-caption-regular'],
  's-column': [
  // Typography
  'typography-caption-regular']
};

var script = defineComponent({
  name: 'OrInputBox',
  components: {
    OrInputBoxAddon: script$1
  },
  props: {
    direction: {
      type: String,
      default: () => InputBoxDirection.Row
    },
    variant: {
      type: String,
      default: () => InputBoxVariant.Styled
    },
    size: {
      type: String,
      default: () => InputBoxSize.M
    },
    invalid: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  expose: ['root'],
  emits: ['focus'],
  setup(props) {
    // Refs
    const root = ref();
    // Styles
    const rootStyles = computed(() => ['or-input-box-v3', ...InputBox, ...InputBoxDirections[props.direction], ...InputBoxVariants[props.variant], ...InputBoxSizes[`${props.size}-${props.direction}`]]);
    return {
      root,
      rootStyles
    };
  }
});

const _hoisted_1 = ["invalid", "readonly", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrInputBoxAddon = resolveComponent("OrInputBoxAddon");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles),
    invalid: _ctx.invalid,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled,
    onFocus: _cache[0] || (_cache[0] = $event => _ctx.$emit('focus'))
  }, [renderSlot(_ctx.$slots, "default"), _ctx.$slots.addon ? (openBlock(), createBlock(_component_OrInputBoxAddon, {
    key: 0,
    invalid: _ctx.invalid,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["invalid", "readonly", "disabled"])) : createCommentVNode("v-if", true)], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_1);
}

script.render = render;
script.__file = "src/components/or-input-box-v3/OrInputBox.vue";

export { InputBoxDirection as I, InputBoxVariant as a, InputBoxSize as b, script as s };
